import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useFetchCompanies from '../api/useFetchCompanies';
import type { CustomFieldQuery } from '../api/useFetchCompanies';
import type { AgentApiCompaniesIndex } from '@/types/serializers';
import CompaniesList from '../components/CompaniesList';
import CompaniesSearchBox from '../components/CompaniesSearchBox';
import type { CustomTagMatch } from '@/components/CustomTagMatchMenu';
import CompaniesVisibleChangeBox from '../components/CompaniesVisibleChangeBox';
import { COMPANY_VISIBLE_ITEMS } from '../constants';
import useFetchCompaniesCustomFields from '../api/useFetchCompaniesCustomFields';
import Loading from '@/components/Loading';
import BackToSearchBar from '@/components/BackToSearchBar';
import CompaniesAddModal from '../components/CompaniesAddModal';
import CompaniesSelectionBar, { type BulkActionType } from '../components/CompaniesSelectionBar';
import { MainContents } from '@/components/MainContents';
import CompanyBulkChangeOrganizationUserModal from '../components/CompanyBulkChangeOrganizationUserModal';
import CompanyBulkTagModal from '../components/CompanyBulkTagModal';
import CompanyBulkRemoveTagModal from '../components/CompanyBulkRemoveTagModal';
import CompanyBulkDeleteModal from '../components/CompanyBulkDeleteModal';
import CompanyBulkUpdateCustomFieldsModal from '../components/CompanyBulkUpdateCustomFieldsModal';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useFetchIam from '../../api/useFetchIam';
import useUpdateCompanyProfile from '../api/useUpdateCompanyProfile';
import { showErrorToast } from '../../job_change_processes/lib/toast';

const SEARCH_CONDITIONS_STORAGE_KEY = 'companiesSearchConditions';

type SearchConditions = {
  sort: string;
  query: string;
  industryId: string;
  listedPhase: string;
  address: string;
  description: string;
  organizationUserId: string;
  numberOfEmployees: { min?: number; max?: number };
  establishedDate: { min?: number; max?: number };
  customTagIds: number[];
  customTagMatch: CustomTagMatch;
  customFieldsSearch: { field_id: number; content: unknown }[];
};

const defaultSearchConditions: SearchConditions = {
  sort: 'id',
  query: '',
  industryId: '',
  listedPhase: '',
  address: '',
  description: '',
  organizationUserId: '',
  numberOfEmployees: {},
  establishedDate: {},
  customTagIds: [],
  customTagMatch: 'all',
  customFieldsSearch: [],
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const sanitizeNumberRange = (value: unknown): { min?: number; max?: number } => {
  if (!isPlainObject(value)) return {};
  const result: { min?: number; max?: number } = {};
  if (typeof value.min === 'number' && Number.isFinite(value.min)) result.min = value.min;
  if (typeof value.max === 'number' && Number.isFinite(value.max)) result.max = value.max;
  return result;
};

const sanitizeCustomFieldsSearch = (value: unknown): { field_id: number; content: unknown }[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is { field_id: number; content: unknown } =>
      isPlainObject(item) && typeof item.field_id === 'number'
  );
};

const loadSearchConditionsFromStorage = (): SearchConditions => {
  try {
    const stored = localStorage.getItem(SEARCH_CONDITIONS_STORAGE_KEY);
    if (!stored) return defaultSearchConditions;
    const parsed = JSON.parse(stored);
    if (!isPlainObject(parsed)) return defaultSearchConditions;
    // 古い localStorage や改変された値が流れ込んでも画面を壊さないよう各フィールドを個別に検証
    return {
      sort: typeof parsed.sort === 'string' ? parsed.sort : defaultSearchConditions.sort,
      query: typeof parsed.query === 'string' ? parsed.query : '',
      industryId: typeof parsed.industryId === 'string' ? parsed.industryId : '',
      listedPhase: typeof parsed.listedPhase === 'string' ? parsed.listedPhase : '',
      address: typeof parsed.address === 'string' ? parsed.address : '',
      description: typeof parsed.description === 'string' ? parsed.description : '',
      organizationUserId:
        typeof parsed.organizationUserId === 'string' ? parsed.organizationUserId : '',
      numberOfEmployees: sanitizeNumberRange(parsed.numberOfEmployees),
      establishedDate: sanitizeNumberRange(parsed.establishedDate),
      customTagIds: Array.isArray(parsed.customTagIds)
        ? parsed.customTagIds.filter((v): v is number => typeof v === 'number')
        : [],
      customTagMatch: parsed.customTagMatch === 'any' ? 'any' : 'all',
      customFieldsSearch: sanitizeCustomFieldsSearch(parsed.customFieldsSearch),
    };
  } catch {
    return defaultSearchConditions;
  }
};

const saveSearchConditionsToStorage = (conditions: SearchConditions) => {
  try {
    localStorage.setItem(SEARCH_CONDITIONS_STORAGE_KEY, JSON.stringify(conditions));
  } catch {
    // localStorage 書き込み失敗（容量超過等）は無視して継続
  }
};

export default function CompaniesTemplate() {
  const searchRef = useRef<HTMLDivElement>(null);
  const { data: iam } = useFetchIam();
  const currentUserId = iam?.current_user?.id;
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { mutateAsync: updateCompany } = useUpdateCompanyProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalAutoOpened, setIsModalAutoOpened] = useState(false); // 追加
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isChangeOrgUserModalOpen, setIsChangeOrgUserModalOpen] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpdateCustomFieldsModalOpen, setIsUpdateCustomFieldsModalOpen] = useState(false);
  const [searchConditions, setSearchConditions] = useState<SearchConditions>(
    loadSearchConditionsFromStorage
  );

  useEffect(() => {
    saveSearchConditionsToStorage(searchConditions);
  }, [searchConditions]);

  const { data: customFields } = useFetchCompaniesCustomFields();

  const [visibleFields, setVisibleFields] = useState<string[]>(
    COMPANY_VISIBLE_ITEMS.map((item) => item.type)
  );

  useEffect(() => {
    if (customFields?.records) {
      const defaultVisibleFields = [
        ...COMPANY_VISIBLE_ITEMS.map((item) => item.type),
        ...customFields.records.map((field) => `custom_field_${field.id}`),
      ];
      setVisibleFields(defaultVisibleFields);
    }
  }, [customFields?.records]);

  const searchParams = useMemo(
    () => ({
      sort: searchConditions.sort,
      query: searchConditions.query || undefined,
      industry_id: searchConditions.industryId || undefined,
      listed_phase: searchConditions.listedPhase || undefined,
      address: searchConditions.address || undefined,
      description: searchConditions.description || undefined,
      organization_user_id: searchConditions.organizationUserId || undefined,
      number_of_employees:
        Object.keys(searchConditions.numberOfEmployees).length > 0
          ? searchConditions.numberOfEmployees
          : undefined,
      established_date:
        Object.keys(searchConditions.establishedDate).length > 0
          ? searchConditions.establishedDate
          : undefined,
      custom_tag_ids:
        searchConditions.customTagIds.length > 0 ? searchConditions.customTagIds : undefined,
      custom_tag_match:
        searchConditions.customTagMatch === 'any' ? searchConditions.customTagMatch : undefined,
      custom_fields: searchConditions.customFieldsSearch as CustomFieldQuery[],
    }),
    [
      searchConditions.sort,
      searchConditions.query,
      searchConditions.industryId,
      searchConditions.listedPhase,
      searchConditions.address,
      searchConditions.description,
      searchConditions.organizationUserId,
      searchConditions.numberOfEmployees,
      searchConditions.establishedDate,
      searchConditions.customTagIds,
      searchConditions.customTagMatch,
      searchConditions.customFieldsSearch,
    ]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useFetchCompanies(searchParams);

  const companies = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: AgentApiCompaniesIndex) => page.records);
  }, [data?.pages]);

  const hasSearchConditions = useMemo(() => {
    return !!(
      searchConditions.query ||
      searchConditions.industryId ||
      searchConditions.listedPhase ||
      searchConditions.address ||
      searchConditions.description ||
      searchConditions.organizationUserId ||
      Object.keys(searchConditions.numberOfEmployees).length > 0 ||
      Object.keys(searchConditions.establishedDate).length > 0 ||
      searchConditions.customTagIds.length > 0 ||
      searchConditions.customFieldsSearch.length > 0
    );
  }, [
    searchConditions.query,
    searchConditions.industryId,
    searchConditions.listedPhase,
    searchConditions.address,
    searchConditions.description,
    searchConditions.organizationUserId,
    searchConditions.numberOfEmployees,
    searchConditions.establishedDate,
    searchConditions.customTagIds,
    searchConditions.customFieldsSearch,
  ]);

  // NOTE: データが0件で検索条件がない場合に登録モーダルを表示
  useEffect(() => {
    if (
      !isLoading &&
      companies.length === 0 &&
      !hasSearchConditions &&
      !isModalOpen &&
      !isModalAutoOpened
    ) {
      setIsModalOpen(true);
      setIsModalAutoOpened(true); // 一度だけ自動で開く
    }
  }, [isLoading, companies.length, hasSearchConditions, isModalOpen, isModalAutoOpened]);

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  const handleSearch = (
    newQuery: string,
    newIndustryId: string,
    newListedPhase: string,
    newAddress: string,
    newNumberOfEmployees: { min?: number; max?: number },
    newEstablishedDate: { min?: number; max?: number },
    newDescription?: string,
    newOrganizationUserId?: string,
    newCustomFields?: { field_id: number; content: unknown }[],
    newCustomTagIds?: number[],
    newCustomTagMatch?: CustomTagMatch
  ) => {
    setSearchConditions((prev) => ({
      sort: prev.sort,
      query: newQuery,
      industryId: newIndustryId,
      listedPhase: newListedPhase,
      address: newAddress,
      numberOfEmployees: newNumberOfEmployees,
      establishedDate: newEstablishedDate,
      description: newDescription || '',
      organizationUserId: newOrganizationUserId || '',
      customTagIds: newCustomTagIds || [],
      customTagMatch: newCustomTagMatch ?? 'all',
      customFieldsSearch: newCustomFields || [],
    }));
  };

  const handleSortChange = (newSort: string) => {
    setSearchConditions((prev) => ({
      ...prev,
      sort: newSort,
    }));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsModalAutoOpened(true); // 手動で閉じたら自動表示済み扱いに
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkAction = useCallback((action: BulkActionType) => {
    if (action === 'changeOrganizationUser') {
      setIsChangeOrgUserModalOpen(true);
    } else if (action === 'addTag') {
      setIsAddTagModalOpen(true);
    } else if (action === 'removeTag') {
      setIsRemoveTagModalOpen(true);
    } else if (action === 'updateData') {
      setIsUpdateCustomFieldsModalOpen(true);
    } else if (action === 'deleteData') {
      setIsDeleteModalOpen(true);
    }
  }, []);

  const handleChangeOrgUserSuccess = useCallback(async () => {
    setIsChangeOrgUserModalOpen(false);
    handleDeselectAll();
    // データを再取得
    await refetch();
  }, [handleDeselectAll, refetch]);

  const handleAddTagSuccess = useCallback(async () => {
    setIsAddTagModalOpen(false);
    handleDeselectAll();
    // データを再取得
    await refetch();
  }, [handleDeselectAll, refetch]);

  const handleRemoveTagSuccess = useCallback(async () => {
    setIsRemoveTagModalOpen(false);
    handleDeselectAll();
    // データを再取得
    await refetch();
  }, [handleDeselectAll, refetch]);

  const handleDeleteSuccess = useCallback(async () => {
    setIsDeleteModalOpen(false);
    handleDeselectAll();
    // データを再取得
    await refetch();
  }, [handleDeselectAll, refetch]);

  const handleUpdateCustomFieldsSuccess = useCallback(async () => {
    setIsUpdateCustomFieldsModalOpen(false);
    handleDeselectAll();
    // データを再取得
    await refetch();
  }, [handleDeselectAll, refetch]);

  const handleUpdateAssignees = useCallback(
    async (companyId: number, userIds: number[]) => {
      try {
        await updateCompany({ companyId, organization_user_ids: userIds });
        await refetch();
      } catch (e) {
        showErrorToast('担当者の更新に失敗しました');
      }
    },
    [updateCompany, refetch]
  );

  return (
    <>
      <MainContents>
        <div ref={searchRef}>
          <CompaniesSearchBox
            total={data?.pages?.[0]?.total_count ?? 0}
            onSearch={handleSearch}
            query={searchConditions.query}
            industryId={searchConditions.industryId}
            listedPhase={searchConditions.listedPhase}
            address={searchConditions.address}
            numberOfEmployees={searchConditions.numberOfEmployees}
            establishedDate={searchConditions.establishedDate}
            description={searchConditions.description}
            organizationUserId={searchConditions.organizationUserId}
            customTagIds={searchConditions.customTagIds}
            customTagMatch={searchConditions.customTagMatch}
            customFields={customFields?.records}
            handleOpenModal={handleOpenModal}
          />
          <CompaniesVisibleChangeBox
            sort={searchConditions.sort}
            setSort={handleSortChange}
            visibleFields={visibleFields}
            setVisibleFields={setVisibleFields}
            customFields={customFields?.records ?? []}
          />
        </div>
        <BackToSearchBar targetRef={searchRef} />
        <div className="space-y-4 pt-md pb-8">
          <CompaniesList
            companies={companies}
            visibleFields={visibleFields}
            customFields={customFields?.records ?? []}
            selectedIds={selectedIds}
            onCheckedChange={handleCheckedChange}
            activeUsers={activeUsers}
            currentUserId={currentUserId}
            onUpdateAssignees={handleUpdateAssignees}
          />
          {(isLoading || isFetchingNextPage) && (
            <div className="flex justify-center py-md">
              <Loading size="small" />
            </div>
          )}
        </div>
      </MainContents>
      <CompaniesAddModal isOpen={isModalOpen} onClose={handleCloseModal} />
      <CompanyBulkChangeOrganizationUserModal
        ids={Array.from(selectedIds)}
        open={isChangeOrgUserModalOpen}
        onOpenChange={setIsChangeOrgUserModalOpen}
        onSuccess={handleChangeOrgUserSuccess}
      />
      <CompanyBulkTagModal
        ids={Array.from(selectedIds)}
        open={isAddTagModalOpen}
        onOpenChange={setIsAddTagModalOpen}
        onSuccess={handleAddTagSuccess}
      />
      <CompanyBulkRemoveTagModal
        ids={Array.from(selectedIds)}
        open={isRemoveTagModalOpen}
        onOpenChange={setIsRemoveTagModalOpen}
        onSuccess={handleRemoveTagSuccess}
      />
      <CompanyBulkDeleteModal
        companies={companies.filter((c) => selectedIds.has(c.id))}
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onSuccess={handleDeleteSuccess}
      />
      <CompanyBulkUpdateCustomFieldsModal
        ids={Array.from(selectedIds)}
        open={isUpdateCustomFieldsModalOpen}
        onOpenChange={setIsUpdateCustomFieldsModalOpen}
        onSuccess={handleUpdateCustomFieldsSuccess}
      />
      <CompaniesSelectionBar
        selectedCount={selectedIds.size}
        onDeselectAll={handleDeselectAll}
        onBulkAction={handleBulkAction}
      />
    </>
  );
}
